import React, {useMemo, useRef, useState} from 'react';
import {IonIcon, IonInput, IonItem, IonLabel, IonList, IonModal, IonSpinner, IonText} from '@ionic/react';
import {searchOutline} from 'ionicons/icons';
import classNames from 'classnames';
import {UsuarioParaSelecao} from '../../models/Transferencia';
import styles from './SelecionarUsuarioTransferencia.module.css';

interface SelecionarUsuarioTransferenciaProps {
  label: string;
  usuarios: UsuarioParaSelecao[];
  isLoading: boolean;
  value: string;
  onChange: (uid: string) => void;
  disabled?: boolean;
}

const SelecionarUsuarioTransferencia: React.FC<SelecionarUsuarioTransferenciaProps> = ({
  label, usuarios, isLoading, value, onChange, disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [termo, setTermo] = useState('');
  const buscaRef = useRef<HTMLIonInputElement>(null);

  const usuariosOrdenados = useMemo(
    () => [...usuarios].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', {sensitivity: 'base'})),
    [usuarios],
  );

  const usuarioSelecionado = usuariosOrdenados.find(u => u.uid === value) || null;

  const usuariosFiltrados = useMemo(() => {
    const termoBusca = termo.trim().toLowerCase();
    if (!termoBusca) return usuariosOrdenados;
    return usuariosOrdenados.filter(u => (
      u.nome.toLowerCase().includes(termoBusca) || u.email.toLowerCase().includes(termoBusca)
    ));
  }, [usuariosOrdenados, termo]);

  const abrir = () => {
    if (disabled) return;
    setTermo('');
    setIsOpen(true);
  };

  const escolher = (uid: string) => {
    onChange(uid);
    setIsOpen(false);
  };

  if (isLoading) {
    return (
      <IonItem>
        <IonLabel position="stacked">{label}</IonLabel>
        <div className="flex items-center gap-2 py-2">
          <IonSpinner/>
          <IonText color="medium">Carregando usuários...</IonText>
        </div>
      </IonItem>
    );
  }

  if (usuarios.length === 0) {
    return (
      <IonItem>
        <IonLabel position="stacked">{label}</IonLabel>
        <IonText color="medium" className="py-2 block">
          Nenhum usuário disponível para transferência.
        </IonText>
      </IonItem>
    );
  }

  return (
    <>
      <IonItem button={!disabled} detail={false} disabled={disabled} onClick={abrir}>
        <IonLabel position="stacked">{label}</IonLabel>
        <IonInput
          value={usuarioSelecionado ? `${usuarioSelecionado.nome} (${usuarioSelecionado.email})` : ''}
          placeholder="Selecione um usuário"
          readonly
        />
        <IonIcon icon={searchOutline} slot="end"/>
      </IonItem>

      <IonModal
        isOpen={isOpen}
        onDidDismiss={() => setIsOpen(false)}
        onDidPresent={() => buscaRef.current?.setFocus()}
        className={classNames(styles.modal, 'modal-as-alert')}
      >
        <div className="h-full overflow-y-hidden flex flex-col">
          <div>
            <IonItem>
              <IonInput
                ref={buscaRef}
                value={termo}
                placeholder="Buscar por nome ou e-mail..."
                debounce={20}
                onIonChange={e => setTermo(e.detail.value || '')}
              />
            </IonItem>
          </div>

          <div className="flex-grow overflow-y-auto">
            {usuariosFiltrados.length === 0 ? (
              <div className="flex justify-center py-8">
                <IonText color="medium">Nenhum usuário encontrado.</IonText>
              </div>
            ) : (
              <IonList>
                {usuariosFiltrados.map(usuario => (
                  <IonItem
                    key={usuario.uid}
                    button
                    detail={false}
                    lines="full"
                    onClick={() => escolher(usuario.uid)}
                  >
                    <IonLabel>
                      <div>{usuario.nome}</div>
                      <div className="text-xs text-gray-400">{usuario.email}</div>
                    </IonLabel>
                  </IonItem>
                ))}
              </IonList>
            )}
          </div>
        </div>
      </IonModal>
    </>
  );
};

export default SelecionarUsuarioTransferencia;
